import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { FiEdit2, FiPlus, FiStar, FiTrash2 } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Modal } from '@/design-system/components/Modal';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { useToast } from '@/client/hooks/useToast';
import {
  useMasterDataCategories,
  useCreateMasterDataCategory,
  useUpdateMasterDataCategory,
  useDeleteMasterDataCategory,
} from '@/lib/sdk/master-data-categories/queries';
import { useMasterDataDatasets } from '@/lib/sdk/master-data/queries';
import { DEFAULT_COLUMN_CONFIG } from '@/lib/sdk/master-data-categories/types';
import type { MasterDataCategory, MasterDataColumnConfig } from '@/lib/sdk/master-data-categories/types';

/** Categories saved before column_config existed read back as fully enabled. */
export function resolveColumnConfig(cat?: MasterDataCategory | null): MasterDataColumnConfig {
  return { ...DEFAULT_COLUMN_CONFIG, ...(cat?.column_config ?? {}) };
}

// ─── Category form ─────────────────────────────────────────────────────────────

const categoryFormSchema = z.object({
  name: z.string().min(1, 'Name is required').regex(/^[a-z0-9_]+$/, 'Use lowercase letters, numbers, underscores only'),
  label: z.string().min(1, 'Label is required'),
  description: z.string().optional(),
  label_column_name: z.string().optional(),
  sort_order: z.number().int().min(0),
  show_code: z.boolean(),
  show_parent_key: z.boolean(),
  show_additional_info: z.boolean(),
  show_location: z.boolean(),
  edit_label: z.boolean(),
  edit_parent_key: z.boolean(),
  edit_additional_info: z.boolean(),
  edit_location: z.boolean(),
});

type CategoryFormData = z.infer<typeof categoryFormSchema>;

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editCat?: MasterDataCategory | null;
  orgId: string;
  nextSortOrder: number;
}

function CategoryFormModal({ isOpen, onClose, editCat, orgId, nextSortOrder }: CategoryFormModalProps) {
  const toast = useToast();
  const { mutate: create, isPending: isCreating } = useCreateMasterDataCategory(orgId);
  const { mutate: update, isPending: isUpdating } = useUpdateMasterDataCategory(orgId);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: { name: '', label: '', description: '', sort_order: nextSortOrder, ...DEFAULT_COLUMN_CONFIG },
  });

  useEffect(() => {
    if (isOpen) {
      reset(editCat
        ? { name: editCat.name, label: editCat.label, description: editCat.description ?? '', label_column_name: editCat.label_column_name ?? '', sort_order: editCat.sort_order, ...resolveColumnConfig(editCat) }
        : { name: '', label: '', description: '', label_column_name: '', sort_order: nextSortOrder, ...DEFAULT_COLUMN_CONFIG }
      );
    }
  }, [isOpen, editCat]);

  const handleClose = () => { reset(); onClose(); };

  const onSubmit = (data: CategoryFormData) => {
    const description = data.description?.trim() || null;
    const label_column_name = data.label_column_name?.trim() || null;
    const column_config: MasterDataColumnConfig = {
      show_code: data.show_code,
      show_parent_key: data.show_parent_key,
      show_additional_info: data.show_additional_info,
      show_location: data.show_location,
      edit_label: data.edit_label,
      edit_parent_key: data.edit_parent_key,
      edit_additional_info: data.edit_additional_info,
      edit_location: data.edit_location,
    };
    if (editCat) {
      update(
        { categoryId: editCat.id, dto: { label: data.label, description, label_column_name, sort_order: data.sort_order, column_config } },
        { onSuccess: () => { toast.success('Category updated'); handleClose(); }, onError: (e: any) => toast.error(e.message ?? 'Update failed') }
      );
    } else {
      create(
        { name: data.name, label: data.label, description, label_column_name, sort_order: data.sort_order, column_config },
        { onSuccess: () => { toast.success('Category created'); handleClose(); }, onError: (e: any) => toast.error(e.message ?? 'Create failed') }
      );
    }
  };

  // An unchecked "show" leaves nothing to edit, so the paired edit toggle is disabled.
  const showParentKey = watch('show_parent_key');
  const showAdditionalInfo = watch('show_additional_info');
  const showLocation = watch('show_location');

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editCat ? 'Edit Category' : 'Add Category'}
      size="md"
      scrollBehavior="inside"
      footer={
        <HStack w="full" justify="flex-end" spacing={3}>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          <Button colorScheme="brand" isLoading={isCreating || isUpdating} onClick={handleSubmit(onSubmit)} data-testid="md-cat-form-submit-btn">
            {editCat ? 'Save' : 'Add Category'}
          </Button>
        </HStack>
      }
    >
      <VStack spacing={4} align="stretch">
        <FormControl isInvalid={!!errors.name} isRequired isReadOnly={!!editCat}>
          <FormLabel fontSize="sm" color="text-primary" mb={1}>Name</FormLabel>
          <Text fontSize="xs" color="text-secondary" mb={2}>Snake_case identifier used as the dataset key (e.g. parts, raw_materials)</Text>
          <Input
            {...register('name')}
            placeholder="e.g. parts"
            size="sm"
            bg={editCat ? 'bg-muted' : 'bg-surface'}
            borderColor="border-default"
            _hover={{ borderColor: 'border-hover' }}
            isReadOnly={!!editCat}
            data-testid="md-cat-form-name"
          />
          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.label} isRequired>
          <FormLabel fontSize="sm" color="text-primary" mb={1}>Label</FormLabel>
          <Input
            {...register('label')}
            placeholder="e.g. Parts"
            size="sm"
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: 'border-hover' }}
            data-testid="md-cat-form-label"
          />
          <FormErrorMessage>{errors.label?.message}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="text-primary" mb={1}>Description</FormLabel>
          <Textarea
            {...register('description')}
            placeholder="Optional description"
            size="sm"
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: 'border-hover' }}
            rows={2}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="text-primary" mb={1}>Label Column Name</FormLabel>
          <Text fontSize="xs" color="text-secondary" mb={2}>Custom header for the primary name column (e.g. "PO Number"). Defaults to "Name" when blank.</Text>
          <Input
            {...register('label_column_name')}
            placeholder="Name"
            size="sm"
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: 'border-hover' }}
          />
        </FormControl>

        <FormControl isInvalid={!!errors.sort_order} maxW="100px">
          <FormLabel fontSize="sm" color="text-primary" mb={1}>Order</FormLabel>
          <Input
            {...register('sort_order', { valueAsNumber: true })}
            type="number"
            size="sm"
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: 'border-hover' }}
          />
        </FormControl>

        <Divider borderColor="border-default" />

        <Box>
          <Text fontSize="sm" color="text-primary" fontWeight="medium" mb={1}>Display &amp; Editing</Text>
          <Text fontSize="xs" color="text-secondary" mb={2}>
            Which columns this category shows in the listing, the item form and Import/Export.
          </Text>

          <Grid templateColumns="1fr 56px 56px" columnGap={2} rowGap={1.5} alignItems="center">
            <Box />
            <Text fontSize="xs" color="text-secondary" fontWeight="medium" textAlign="center">Show</Text>
            <Text fontSize="xs" color="text-secondary" fontWeight="medium" textAlign="center">Edit</Text>

            <Text fontSize="sm" color="text-primary">Code</Text>
            <Box textAlign="center">
              <Checkbox {...register('show_code')} colorScheme="brand" data-testid="md-cat-form-show-code" />
            </Box>
            <Tooltip label="Codes are always generated automatically" placement="top">
              <Text fontSize="xs" color="text-muted" textAlign="center">auto</Text>
            </Tooltip>

            <Text fontSize="sm" color="text-primary">Name</Text>
            <Tooltip label="The primary name column is always shown" placement="top">
              <Text fontSize="xs" color="text-muted" textAlign="center">always</Text>
            </Tooltip>
            <Box textAlign="center">
              <Checkbox {...register('edit_label')} colorScheme="brand" data-testid="md-cat-form-edit-label" />
            </Box>

            <Text fontSize="sm" color="text-primary">Belongs To</Text>
            <Box textAlign="center">
              <Checkbox {...register('show_parent_key')} colorScheme="brand" data-testid="md-cat-form-show-parent-key" />
            </Box>
            <Box textAlign="center">
              <Checkbox {...register('edit_parent_key')} colorScheme="brand" isDisabled={!showParentKey} data-testid="md-cat-form-edit-parent-key" />
            </Box>

            <Text fontSize="sm" color="text-primary">Additional Info</Text>
            <Box textAlign="center">
              <Checkbox {...register('show_additional_info')} colorScheme="brand" data-testid="md-cat-form-show-additional-info" />
            </Box>
            <Box textAlign="center">
              <Checkbox {...register('edit_additional_info')} colorScheme="brand" isDisabled={!showAdditionalInfo} data-testid="md-cat-form-edit-additional-info" />
            </Box>

            <Text fontSize="sm" color="text-primary">Location (Lat/Lng)</Text>
            <Box textAlign="center">
              <Checkbox {...register('show_location')} colorScheme="brand" data-testid="md-cat-form-show-location" />
            </Box>
            <Box textAlign="center">
              <Checkbox {...register('edit_location')} colorScheme="brand" isDisabled={!showLocation} data-testid="md-cat-form-edit-location" />
            </Box>
          </Grid>
        </Box>
      </VStack>
    </Modal>
  );
}

// ─── CategoryManager ───────────────────────────────────────────────────────────

interface CategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
}

export function CategoryManager({ isOpen, onClose, orgId }: CategoryManagerProps) {
  const toast = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<MasterDataCategory | null>(null);
  const [deletingCat, setDeletingCat] = useState<MasterDataCategory | null>(null);

  const { data: cats = [], isLoading } = useMasterDataCategories(orgId);
  const { data: datasets = [] } = useMasterDataDatasets(orgId);
  const { mutate: deleteCat, isPending: isDeleting } = useDeleteMasterDataCategory(orgId);
  const { mutate: updateCat } = useUpdateMasterDataCategory(orgId);

  const handleAdd = () => { setEditingCat(null); setIsFormOpen(true); };
  const handleEdit = (cat: MasterDataCategory) => { setEditingCat(cat); setIsFormOpen(true); };
  const handleDeleteConfirm = () => {
    if (!deletingCat) return;
    deleteCat(deletingCat.id, {
      onSuccess: () => { toast.success('Category deleted'); setDeletingCat(null); },
      onError: (e: any) => toast.error(e.message ?? 'Delete failed'),
    });
  };

  const handleSetDefault = (cat: MasterDataCategory) => {
    updateCat(
      // PATCH overwrites every field it receives, so resend column_config too.
      { categoryId: cat.id, dto: { label: cat.label, description: cat.description, label_column_name: cat.label_column_name, sort_order: cat.sort_order, is_default: true, column_config: resolveColumnConfig(cat) } },
      {
        onSuccess: () => toast.success(`"${cat.label}" set as default`),
        onError: (e: any) => toast.error(e.message ?? 'Failed to set default'),
      }
    );
  };

  const nextSortOrder = cats.length > 0 ? Math.max(...cats.map((c) => c.sort_order)) + 1 : 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Manage Categories"
        size="lg"
        scrollBehavior="inside"
        footer={
          <HStack w="full" justify="space-between">
            <Button size="sm" leftIcon={<FiPlus />} colorScheme="brand" onClick={handleAdd} data-testid="md-category-add-btn">Add Category</Button>
            <Button variant="ghost" onClick={onClose} data-testid="md-category-done-btn">Done</Button>
          </HStack>
        }
      >
        <VStack spacing={3} align="stretch" minH="200px">
          {isLoading && <Text color="text-secondary" fontSize="sm">Loading…</Text>}
          {!isLoading && cats.length === 0 && (
            <Text color="text-secondary" fontSize="sm" fontStyle="italic">
              No categories defined yet. Add categories to pre-populate the dataset selector before entering data.
            </Text>
          )}
          {cats.map((cat) => {
            const hasData = datasets.includes(cat.name);
            return (
              <Box key={cat.id} p={3} borderWidth="1px" borderColor={cat.is_default ? 'brand.400' : 'border-default'} borderRadius="md" bg="bg-surface" data-testid={`md-category-item-${cat.name}`}>
                <HStack justify="space-between">
                  <Box>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontWeight="medium" fontSize="sm" color="text-primary">{cat.label}</Text>
                      {cat.is_default && <Badge colorScheme="brand" fontSize="xs">default</Badge>}
                      {hasData && <Badge colorScheme="green" fontSize="xs">has data</Badge>}
                    </HStack>
                    <Text fontFamily="mono" fontSize="xs" color="text-secondary">{cat.name}</Text>
                    {cat.description && (
                      <Text fontSize="xs" color="text-secondary" mt={0.5}>{cat.description}</Text>
                    )}
                  </Box>
                  <HStack spacing={1}>
                    {!cat.is_default && (
                      <Tooltip label="Set as default" placement="top">
                        <IconButton aria-label="Set as default" icon={<FiStar />} size="sm" variant="ghost" onClick={() => handleSetDefault(cat)} data-testid={`md-category-default-btn-${cat.name}`} />
                      </Tooltip>
                    )}
                    <IconButton aria-label="Edit" icon={<FiEdit2 />} size="sm" variant="ghost" onClick={() => handleEdit(cat)} data-testid={`md-category-edit-btn-${cat.name}`} />
                    <IconButton aria-label="Delete" icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red" onClick={() => setDeletingCat(cat)} data-testid={`md-category-delete-btn-${cat.name}`} />
                  </HStack>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </Modal>

      <CategoryFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        editCat={editingCat}
        orgId={orgId}
        nextSortOrder={nextSortOrder}
      />

      <ConfirmDialog
        isOpen={!!deletingCat}
        onClose={() => setDeletingCat(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        message={
          datasets.includes(deletingCat?.name ?? '')
            ? `"${deletingCat?.label}" has existing data rows. Deleting the category will not remove the rows, but the category will no longer appear in the selector unless rows exist. Continue?`
            : `Delete category "${deletingCat?.label}"?`
        }
        confirmText="Delete"
        colorScheme="red"
        isLoading={isDeleting}
      />
    </>
  );
}
